import { Banner, Button, Spinner } from "@salesko/design-system";
import { CheckCircle2, Database, Download, FileDown, FileUp, Upload } from "lucide-react";
import type { ChangeEvent, ReactNode } from "react";
import { useI18n } from "../../i18n";
import { SettingsNote, SettingsSection } from "./SettingsSection";
import { useGraphChangeSets } from "../hooks/useGraphChangeSets";
import { useGraphFrameIo } from "../hooks/useGraphFrameIo";
import type { ChangeSetActionVariables, ChangeSetLifecycleState, FrameIoState } from "../types";

type DataToolsPanelProps = {
  activeFrameRecordId: string | null;
  enabled: boolean;
};

export function DataToolsPanel({ activeFrameRecordId, enabled }: DataToolsPanelProps) {
  const { t } = useI18n();
  const {
    applyChangeSet,
    changeSetState,
    csvFileInputRef,
    csvImportPending,
    dismissChangeSetLifecycle,
    handleCsvFileChange,
    handleCsvImportClick
  } = useGraphChangeSets({
    copy: t.changeSet,
    frameRecordId: enabled ? activeFrameRecordId : null,
    onFrameMutated: (_frame, commit) => openWorkspaceFrame(commit.id),
    onInvalidateFrames: () => {}
  });

  const {
    dismissFrameIoStatus,
    frameExportPending,
    frameImportPending,
    frameIoState,
    handleFrameExportClick,
    handleJsonFileChange,
    handleJsonImportClick,
    handleObsidianFileChange,
    handleObsidianImportClick,
    jsonFileInputRef,
    obsidianFileInputRef
  } = useGraphFrameIo({
    copy: t.frameIo,
    frameRecordId: enabled ? activeFrameRecordId : null,
    onImportedFrame: (_frame, commit) => openWorkspaceFrame(commit.id),
    onResetChangeSetState: dismissChangeSetLifecycle,
    sessionId: "settings-data-tools"
  });

  const frameScopedDisabled = !enabled || !activeFrameRecordId;
  const importDisabled = !enabled || frameImportPending;
  const exportDisabled = frameScopedDisabled || frameExportPending;

  return (
    <SettingsSection
      ariaLabel={t.dataTools.ariaLabel}
      id="settings-data-tools"
      title={t.dataTools.title}
      trailing={<Database size={15} />}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <ToolGroup icon={<FileUp size={16} />} title={t.dataTools.import}>
          <Button
            disabled={frameScopedDisabled || csvImportPending}
            iconLeft={csvImportPending ? <Spinner size={14} /> : <Upload size={15} />}
            onClick={handleCsvImportClick}
            size="sm"
            variant="secondary"
          >
            {t.dataTools.csvChangeSet}
          </Button>
          <Button
            disabled={importDisabled}
            iconLeft={
              frameIoState.status === "importing" && frameIoState.format === "json" ? (
                <Spinner size={14} />
              ) : (
                <Upload size={15} />
              )
            }
            onClick={handleJsonImportClick}
            size="sm"
            variant="secondary"
          >
            {t.dataTools.jsonFrame}
          </Button>
          <Button
            disabled={importDisabled}
            iconLeft={
              frameIoState.status === "importing" && frameIoState.format === "obsidian" ? (
                <Spinner size={14} />
              ) : (
                <Upload size={15} />
              )
            }
            onClick={handleObsidianImportClick}
            size="sm"
            variant="secondary"
          >
            {t.dataTools.obsidianFiles}
          </Button>
        </ToolGroup>

        <ToolGroup icon={<FileDown size={16} />} title={t.dataTools.export}>
          <Button
            disabled={exportDisabled}
            iconLeft={
              frameIoState.status === "exporting" && frameIoState.format === "json" ? (
                <Spinner size={14} />
              ) : (
                <Download size={15} />
              )
            }
            onClick={() => handleFrameExportClick("json")}
            size="sm"
            variant="secondary"
          >
            {t.dataTools.jsonFrame}
          </Button>
          <Button
            disabled={exportDisabled}
            iconLeft={
              frameIoState.status === "exporting" && frameIoState.format === "obsidian" ? (
                <Spinner size={14} />
              ) : (
                <Download size={15} />
              )
            }
            onClick={() => handleFrameExportClick("obsidian")}
            size="sm"
            variant="secondary"
          >
            {t.dataTools.obsidianFiles}
          </Button>
        </ToolGroup>
      </div>

      <SettingsNote>
        {activeFrameRecordId ? t.dataTools.activeFrame(activeFrameRecordId) : t.dataTools.needsCommittedFrame}
      </SettingsNote>

      <FrameIoPanelStatus state={frameIoState} onDismiss={dismissFrameIoStatus} />
      <ChangeSetPanelStatus state={changeSetState} onApply={applyChangeSet} onDismiss={dismissChangeSetLifecycle} />

      <input
        ref={csvFileInputRef}
        aria-hidden="true"
        className="visually-hidden"
        type="file"
        accept=".csv,text/csv"
        onChange={handleCsvFileChange as (event: ChangeEvent<HTMLInputElement>) => void}
        tabIndex={-1}
      />
      <input
        ref={jsonFileInputRef}
        aria-hidden="true"
        className="visually-hidden"
        type="file"
        accept=".json,application/json"
        onChange={handleJsonFileChange as (event: ChangeEvent<HTMLInputElement>) => void}
        tabIndex={-1}
      />
      <input
        ref={obsidianFileInputRef}
        aria-hidden="true"
        className="visually-hidden"
        type="file"
        accept=".json,.md,.markdown,application/json,text/markdown,text/plain"
        multiple
        onChange={handleObsidianFileChange as (event: ChangeEvent<HTMLInputElement>) => void}
        tabIndex={-1}
      />
    </SettingsSection>
  );
}

function ToolGroup({ children, icon, title }: { children: ReactNode; icon: ReactNode; title: string }) {
  return (
    <div
      style={{
        display: "grid",
        gap: 10,
        minWidth: 0,
        justifyItems: "start",
        padding: 12,
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)"
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          color: "var(--text-body)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-body-sm)",
          fontWeight: "var(--fw-bold)"
        }}
      >
        {icon}
        {title}
      </span>
      {children}
    </div>
  );
}

function FrameIoPanelStatus({ onDismiss, state }: { onDismiss: () => void; state: FrameIoState }) {
  const { t } = useI18n();
  if (state.status === "idle") {
    return null;
  }

  if (state.status === "error") {
    return <PanelStatus message={state.message} tone="error" onDismiss={onDismiss} />;
  }

  if (state.status === "exporting") {
    return <PanelStatus message={t.frameIo.exporting(state.format)} />;
  }

  if (state.status === "importing") {
    return <PanelStatus message={t.frameIo.importingSimple(state.fileName)} />;
  }

  if (state.status === "exported") {
    return <PanelStatus message={t.frameIo.downloadedFile(state.filename)} onDismiss={onDismiss} />;
  }

  if (state.status === "imported") {
    return <PanelStatus message={t.frameIo.importedOpening(state.fileName)} onDismiss={onDismiss} />;
  }

  if (state.status === "sharing") {
    return <PanelStatus message={t.frameIo.creatingShareLinkShort} />;
  }

  return <PanelStatus message={t.frameIo.shareLinkReady} onDismiss={onDismiss} />;
}

function ChangeSetPanelStatus({
  onApply,
  onDismiss,
  state
}: {
  onApply: (variables: ChangeSetActionVariables) => void;
  onDismiss: () => void;
  state: ChangeSetLifecycleState;
}) {
  const { t } = useI18n();
  if (state.status === "idle") {
    return null;
  }

  if (state.status === "error") {
    return <PanelStatus message={state.message} tone="error" onDismiss={onDismiss} />;
  }

  if (state.status === "importing") {
    return <PanelStatus message={t.frameIo.importingSimple(state.fileName)} />;
  }

  if (state.status === "pending") {
    const variables = {
      changeSetId: state.response.changeSet.id,
      frameRecordId: state.response.changeSet.frameRecordId,
      ...(state.importSummary ? { importSummary: state.importSummary } : {}),
      sourceLabel: state.sourceLabel
    };
    return (
      <PanelStatus
        action={
          <Button iconLeft={<CheckCircle2 size={14} />} onClick={() => onApply(variables)} size="sm" variant="secondary">
            {t.changeSet.apply}
          </Button>
        }
        message={t.changeSet.pendingPanel(state.response.changeSet.id.slice(0, 8), state.sourceLabel)}
      />
    );
  }

  if (state.status === "applying" || state.status === "reverting") {
    return (
      <PanelStatus
        message={t.changeSet.updatingSource(state.status === "applying" ? t.changeSet.apply : t.changeSet.revert, state.sourceLabel)}
      />
    );
  }

  return <PanelStatus message={t.changeSet.sourceUpdated(state.sourceLabel)} onDismiss={onDismiss} />;
}

function PanelStatus({
  action,
  message,
  onDismiss,
  tone = "neutral"
}: {
  action?: ReactNode;
  message: string;
  onDismiss?: () => void;
  tone?: "error" | "neutral";
}) {
  const { t } = useI18n();
  return (
    <Banner
      action={
        action || onDismiss ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {action}
            {onDismiss ? (
              <Button onClick={onDismiss} size="sm" variant="ghost">
                {t.common.dismiss}
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
      status={tone === "error" ? "error" : "info"}
    >
      {message}
    </Banner>
  );
}

function openWorkspaceFrame(frameRecordId: string) {
  window.location.assign(`/app?frame=${encodeURIComponent(frameRecordId)}`);
}
